import { NavLink, useNavigate } from 'react-router-dom';
import { AnimatedOutlet } from '../ui/PageTransition';
import { useState, useEffect, useRef } from 'react';
import {
  LayoutDashboard, Bot, Users, Terminal, BarChart3, Dumbbell,
  Settings, CreditCard, ChevronLeft, ChevronRight,
  Bell, LogOut, Menu, Store, Brain, X, Plug, Share2, Zap,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import { ToastContainer } from '../ui/Toast';
import { cn } from '../../lib/utils';
import { dashboard } from '../../lib/api';

const NAV_ITEMS = [
  { to: '/dashboard',    icon: LayoutDashboard, label: 'Dashboard'    },
  { to: '/agents',       icon: Bot,             label: 'Agents'       },
  { to: '/leads',        icon: Users,           label: 'Leads'        },
  { to: '/command',      icon: Terminal,        label: 'Command'      },
  { to: '/analytics',    icon: BarChart3,       label: 'Analytics'    },
  { to: '/training',     icon: Dumbbell,        label: 'Training'     },
  { to: '/social',       icon: Share2,          label: 'Social'       },
  { to: '/marketplace',  icon: Store,           label: 'Marketplace'  },
  { to: '/brain',        icon: Brain,           label: 'Agent Brain'  },
  { to: '/integrations', icon: Plug,            label: 'Integrations' },
];

const BOTTOM_NAV = [
  { to: '/settings', icon: Settings,   label: 'Settings' },
  { to: '/billing',  icon: CreditCard, label: 'Billing'  },
];

const tierLabel: Record<string, string> = {
  free_trial: 'Free Trial',
  free:       'Free',
  starter:    'Starter',
  growth:     'Growth',
  scale:      'Scale',
  pro:        'Pro',
  enterprise: 'Enterprise',
  founder:    'Founder',
};

const tierColor: Record<string, string> = {
  free_trial:  'text-brand-400',
  free:        'text-ink-mid',
  starter:     'text-brand-400',
  growth:      'text-cyber-green',
  scale:       'text-cyber-yellow',
  pro:         'text-brand-300',
  enterprise:  'text-brand-200',
  founder:     'text-cyber-yellow',
};

function SidebarContent({
  narrow,
  mobile,
  onClose,
  onCollapse,
  user,
  onLogout,
}: {
  narrow: boolean;
  mobile: boolean;
  onClose?: () => void;
  onCollapse?: () => void;
  user: { name: string; email: string; tier: string } | null;
  onLogout: () => void;
}) {
  const isTrial = user?.tier === 'free_trial' || user?.tier === 'free';

  return (
    <aside
      className={cn(
        'flex flex-col h-screen border-r transition-all duration-150',
        'bg-bot-surface border-bot-mid',
        mobile ? 'w-64' : narrow ? 'w-14' : 'w-56',
      )}
    >
      {/* ── Logo ─────────────────────────────────────────────── */}
      <div className={cn('flex items-center h-14 px-3 border-b border-bot-mid shrink-0', narrow && 'justify-center')}>
        {!narrow ? (
          <>
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <div className="w-7 h-7 rounded-lg bg-brand-500 flex items-center justify-center shrink-0">
                <span className="text-white font-bold text-xs">CA</span>
              </div>
              <span className="text-sm font-semibold text-ink-full tracking-tight truncate">CoordinAGENTS</span>
            </div>
            {!mobile && (
              <button onClick={onCollapse} className="text-ink-dim hover:text-ink-mid transition-colors p-1 shrink-0 rounded">
                <ChevronLeft size={14} />
              </button>
            )}
          </>
        ) : (
          <div className="relative flex items-center justify-center w-full">
            <div className="w-7 h-7 rounded-lg bg-brand-500 flex items-center justify-center">
              <span className="text-white font-bold text-xs">CA</span>
            </div>
            <button onClick={onCollapse} className="absolute -right-1 text-ink-dim hover:text-ink-mid transition-colors p-1 rounded">
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* ── Main nav ─────────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {!narrow && (
          <div className="px-2 pb-2 pt-0.5">
            <span className="text-[0.6875rem] font-medium text-ink-dim uppercase tracking-wider">Navigation</span>
          </div>
        )}
        {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => mobile && onClose?.()}
              title={narrow ? label : undefined}
              className={({ isActive }) =>
                cn('nav-item', isActive && 'active', narrow && 'justify-center px-0 py-2')
              }
            >
              <Icon size={narrow ? 16 : 15} className="shrink-0" />
              {!narrow && <span className="flex-1 truncate">{label}</span>}
            </NavLink>
        ))}
      </nav>

      {/* ── Bottom ───────────────────────────────────────────── */}
      <div className="px-2 py-3 border-t border-bot-mid space-y-0.5">
        {BOTTOM_NAV.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => mobile && onClose?.()}
            title={narrow ? label : undefined}
            className={({ isActive }) =>
              cn('nav-item', isActive && 'active', narrow && 'justify-center px-0 py-2')
            }
          >
            <Icon size={narrow ? 16 : 15} className="shrink-0" />
            {!narrow && <span className="flex-1 truncate">{label}</span>}
          </NavLink>
        ))}

        <button
          onClick={onLogout}
          title={narrow ? 'Logout' : undefined}
          className={cn(
            'nav-item w-full text-left hover:!text-cyber-red hover:!bg-cyber-red-dim',
            narrow && 'justify-center px-0 py-2',
          )}
        >
          <LogOut size={narrow ? 16 : 15} className="shrink-0" />
          {!narrow && <span className="flex-1 truncate">Logout</span>}
        </button>

        {/* ── Upgrade CTA for free tier ──────────── */}
        {!narrow && isTrial && (
          <NavLink
            to="/billing"
            onClick={() => mobile && onClose?.()}
            className="flex items-center gap-2 px-3 py-2 mt-1 rounded-lg border border-brand-500/30 bg-brand-500/5 hover:bg-brand-500/10 transition-all"
          >
            <Zap size={14} className="text-brand-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-brand-400">Upgrade Plan</p>
              <p className="text-[0.6875rem] text-ink-dim">Unlock all features</p>
            </div>
          </NavLink>
        )}

        {/* ── User chip ──────────────────────────────────────── */}
        {!narrow && user && (
          <div className="mt-2 pt-2 border-t border-bot-border">
            <div className="flex items-center gap-2.5 px-2 py-1.5">
              <div className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center bg-brand-500/15 text-brand-400 text-xs font-semibold">
                {(user.name || user.email).charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-ink-full truncate" title={user.name || user.email}>{user.name || user.email}</p>
                <p className={cn('text-[0.6875rem] font-medium', tierColor[user.tier] ?? 'text-ink-mid')}>
                  {tierLabel[user.tier] ?? user.tier}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  read_at: number | null;
  created_at: number;
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await dashboard.notifications();
      setNotifications((res.notifications ?? []) as unknown as NotificationItem[]);
      setUnread(res.unreadCount ?? 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function markRead(id: string) {
    try {
      await dashboard.markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: Date.now() } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  }

  async function markAll() {
    try {
      await dashboard.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read_at: Date.now() })));
      setUnread(0);
    } catch { /* ignore */ }
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
        className="p-1.5 text-ink-mid hover:text-ink-full rounded-md hover:bg-bot-raised transition-all relative"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center rounded-full bg-cyber-red text-white text-[0.625rem] font-medium leading-none">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-[calc(100vw-1rem)] max-w-[320px] sm:w-80 bg-bot-surface border border-bot-mid rounded-lg z-50 shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-bot-mid">
            <span className="text-xs font-semibold text-ink-full">Notifications</span>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAll} className="text-xs text-brand-400 hover:text-brand-300 transition-colors">
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-ink-dim hover:text-ink-full transition-colors rounded p-0.5">
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="py-8 text-center text-ink-dim text-xs">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-ink-dim text-xs">No notifications</div>
            ) : (
              notifications.slice(0, 20).map(n => (
                <div
                  key={n.id}
                  onClick={() => !n.read_at && markRead(n.id)}
                  className={cn(
                    'px-3 py-2.5 border-b border-bot-border cursor-pointer transition-colors',
                    n.read_at ? 'opacity-50 hover:opacity-70' : 'hover:bg-bot-base',
                  )}
                >
                  <div className="flex items-start gap-2">
                    {!n.read_at && <div className="w-1.5 h-1.5 rounded-full bg-brand-400 mt-1.5 shrink-0" />}
                    <div className={cn('min-w-0', n.read_at && 'ml-3.5')}>
                      <p className="text-xs text-ink-full truncate">{n.title}</p>
                      <p className="text-xs text-ink-dim truncate mt-0.5">{n.message}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-bot-base">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex shrink-0 relative">
        <SidebarContent
          narrow={collapsed}
          mobile={false}
          user={user}
          onLogout={handleLogout}
          onCollapse={() => setCollapsed(!collapsed)}
        />
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full z-10">
            <SidebarContent
              narrow={false}
              mobile
              user={user}
              onLogout={handleLogout}
              onClose={() => setMobileOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-12 flex items-center justify-between px-3 sm:px-4 border-b border-bot-mid bg-bot-surface shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            className="lg:hidden text-ink-mid hover:text-ink-full transition-colors p-1.5 rounded-md hover:bg-bot-raised"
          >
            <Menu size={18} />
          </button>

          <div className="hidden lg:flex items-center gap-2">
            <div className="led led-green" />
            <span className="text-xs text-ink-mid">All systems operational</span>
          </div>

          <div className="flex lg:hidden items-center gap-1.5">
            <div className="led led-green" />
            <span className="text-xs text-ink-mid">Online</span>
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <NotificationBell />
            {user && (
              <div className="flex items-center gap-2 border-l border-bot-mid pl-2 sm:pl-3 ml-1">
                <div className="w-6 h-6 rounded-full flex items-center justify-center bg-brand-500/15 text-brand-400 text-xs font-medium">
                  {(user.name || user.email).charAt(0).toUpperCase()}
                </div>
                <span className="hidden sm:block text-xs text-ink-mid truncate max-w-[100px] md:max-w-[140px]">
                  {user.email}
                </span>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-bot-base">
          <AnimatedOutlet />
        </main>
      </div>

      <ToastContainer />
    </div>
  );
}
